import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { ClockCounterClockwise } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTION_STYLE = {
  confirmed: "bg-green-100 text-green-800 border-green-300",
  false_positive: "bg-gray-100 text-gray-700 border-gray-300",
  needs_review: "bg-yellow-100 text-yellow-800 border-yellow-300",
};

export default function FraudFeedbackHistory({ claimId, refreshKey }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => axios.get(`${API}/fraud/feedback/${claimId}`).then(r => setRows(r.data)).catch(() => {}), [claimId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const entries = rows.flatMap(r => (r.history || [{ action: r.action, notes: r.notes, investigator_email: r.investigator_email, at: r.updated_at }])
    .map(h => ({ ...h, alert_code: r.alert_code, alert_name: r.alert_name, page_no: r.page_no, key: `${r.alert_key}-${h.at}` })))
    .sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  if (!entries.length) {
    return <p className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-md p-4 text-center" data-testid="feedback-history-empty">No investigator feedback recorded yet. Use Confirm / False positive / Needs review on an alert.</p>;
  }

  return (
    <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-72 overflow-auto" data-testid="feedback-history">
      {entries.map(e => (
        <div key={e.key} className="px-3 py-2 flex items-start gap-3 text-xs" data-testid={`feedback-entry-${e.alert_code}-${e.action}`}>
          <ClockCounterClockwise size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={`text-[10px] capitalize ${ACTION_STYLE[e.action] || ""}`}>{(e.action || "").replace("_", " ")}</Badge>
              <span className="font-mono text-gray-400">{e.alert_code}</span>
              <span className="font-medium text-gray-900">{e.alert_name}</span>
              {e.page_no && <span className="text-gray-500">· Page {e.page_no}</span>}
            </div>
            {e.notes && <p className="text-gray-700 mt-1 italic" data-testid="feedback-note">“{e.notes}”</p>}
            <p className="text-[10px] text-gray-400 mt-0.5">{e.investigator_email} · {e.at ? new Date(e.at).toLocaleString() : ""}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
